import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import customerOrderService from '../services/customerOrderService';
import { useLanguage, t } from '../context/LanguageContext';
import { useOrderNotifications } from '../context/OrderNotificationContext';
import LotCodeModal from '../components/LotCodeModal';
import SearchableSelect from '../components/SearchableSelect';
import DeliveryInfoModal from '../components/DeliveryInfoModal';
import CustomizationDetails from '../components/CustomizationDetails';

const API_URL = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Inject keyframe animation once into document head
const STYLE_ID = 'factory-orders-pulse-style';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes order-pulse {
      0%   { box-shadow: 0 0 0 2px #ffc107, 0 0 10px 2px rgba(255,193,7,0.6); }
      50%  { box-shadow: 0 0 0 4px #ffc107, 0 0 24px 8px rgba(255,193,7,0.35); }
      100% { box-shadow: 0 0 0 2px #ffc107, 0 0 10px 2px rgba(255,193,7,0.6); }
    }
    .order-card-new {
      border: 2px solid #ffc107 !important;
      animation: order-pulse 1.4s ease-in-out infinite;
    }
  `;
  document.head.appendChild(el);
}

const STATUS_CONFIG = {
  confirmed: {
    en: 'Confirmed', es: 'Confirmado', color: 'info',
    nextStatus: 'ready', nextEn: 'Mark Completed', nextEs: 'Marcar Completado', nextColor: 'success',
  },
  ready: {
    en: 'Completed', es: 'Completado', color: 'success',
    nextStatus: 'shipped', nextEn: 'Mark Shipped', nextEs: 'Marcar Enviado', nextColor: 'secondary',
  },
};

const HISTORY_STATUS_LABELS = {
  shipped: { en: 'Shipped', es: 'Enviado', color: 'secondary' },
  invoiced: { en: 'Invoiced', es: 'Facturado', color: 'dark' },
  cancelled: { en: 'Cancelled', es: 'Cancelado', color: 'danger' },
};

// ── Mark Shipped modal — optional delivery employee assignment ─────────────────
function MarkShippedModal({ order, onClose, onShipped, lang }) {
  const [employees, setEmployees] = useState([]);
  const [empId,     setEmpId]     = useState(order.deliveryEmployeeId ? String(order.deliveryEmployeeId) : '');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/time-tracking/employees?status=active`, auth())
      .then(r => setEmployees(Array.isArray(r.data?.employees) ? r.data.employees : []))
      .catch(() => {});
  }, []);

  const handleShip = async () => {
    setSaving(true); setError('');
    try {
      if (empId) await customerOrderService.assignDelivery(order.id, Number(empId));
      await customerOrderService.transitionOrder(order.id, 'shipped');
      onShipped();
      onClose();
    } catch (err) {
      const reason = err.response?.data?.error || err.response?.data?.message;
      const base = t('Failed to mark as shipped.', 'Error al marcar como enviado.', lang);
      setError(reason ? `${base} (${reason})` : base);
    } finally {
      setSaving(false);
    }
  };

  const customer = order.eventId
    ? (order.Event?.name || `Event #${order.eventId}`)
    : (order.submittedVia === 'shopify' ? (order.deliveryContactName || '—') : (order.Customer?.name || '—'));

  const opts = [
    { value: '', label: t('— No driver (pickup / direct) —', '— Sin conductor (recogida / directo) —', lang) },
    ...employees.map(e => ({ value: String(e.id), label: `${e.firstName} ${e.lastName}` })),
  ];

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" style={{ fontSize: 15 }}>
              {t('Mark as Shipped', 'Marcar como Enviado', lang)} — #{order.orderNumber}
            </h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              <strong>{customer}</strong>
              {order.Items?.length > 0 && (
                <span className="ms-2">
                  · {order.Items.map(i => `${i.Product?.name || '—'} ×${i.quantity}`).join(', ')}
                </span>
              )}
            </p>

            <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
              {t('Delivery Driver', 'Conductor de entrega', lang)}
              <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>
                ({t('optional', 'opcional', lang)})
              </span>
            </label>
            <SearchableSelect
              options={opts}
              value={empId}
              onChange={setEmpId}
              placeholder={t('Select driver or leave blank for pickup…', 'Seleccionar conductor o dejar vacío para recogida…', lang)}
            />
            <p className="text-muted mt-2 mb-0" style={{ fontSize: 11 }}>
              {t(
                'If assigned, the driver will see this order in their portal Deliveries tab.',
                'Si se asigna, el conductor verá este pedido en la pestaña Entregas del portal.',
                lang
              )}
            </p>

            {error && <div className="alert alert-danger mt-3 py-2 mb-0" style={{ fontSize: 13 }}>{error}</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              {t('Cancel', 'Cancelar', lang)}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleShip} disabled={saving}>
              {saving
                ? <><span className="spinner-border spinner-border-sm me-1" />{t('Saving…', 'Guardando…', lang)}</>
                : t('Mark Shipped', 'Marcar Enviado', lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, onAdvance, onAcknowledge, onMarkReady, onMarkShipped, onOpenLotCodes, onOpenDeliveryInfo, lang }) {
  const cfg = STATUS_CONFIG[order.status];
  const [busy, setBusy] = useState(false);
  const [acking, setAcking] = useState(false);

  const hasLotCodes   = (order.LotCodes?.length || 0) > 0;
  const lotCodesLocked = (order.LotCodes || []).some(lc => lc.status === 'approved');

  const handleAdvance = async () => {
    if (!cfg?.nextStatus) return;
    // confirmed → ready: delegate to parent (lot code modal)
    if (cfg.nextStatus === 'ready' && onMarkReady) {
      onMarkReady(order);
      return;
    }
    // ready → shipped: delegate to parent (optional driver assignment modal)
    if (cfg.nextStatus === 'shipped' && onMarkShipped) {
      onMarkShipped(order);
      return;
    }
    setBusy(true);
    try {
      await customerOrderService.transitionOrder(order.id, cfg.nextStatus);
      onAdvance();
    } finally {
      setBusy(false);
    }
  };

  const handleAcknowledge = async () => {
    setAcking(true);
    try {
      await customerOrderService.acknowledgeOrder(order.id);
      onAcknowledge();
    } finally {
      setAcking(false);
    }
  };

  return (
    <div className={`card h-100${!order.acknowledgedAt ? ' order-card-new' : ' shadow-sm'}`}>
      <div className={`card-header bg-${cfg?.color || 'secondary'} bg-opacity-10 d-flex justify-content-between align-items-center`}>
        <span className="fw-bold">
          {order.eventId
            ? <>{order.Event?.name || `Event #${order.eventId}`} <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>Direct to Consumer</span></>
            : order.submittedVia === 'shopify'
              ? (
                <>
                  {order.deliveryContactName || '—'} <span className="badge bg-success" style={{ fontSize: 10 }}>Shopify</span>{' '}
                  <button
                    className="btn btn-link btn-sm p-0 align-baseline"
                    title="Delivery info (contact, address, delivery method)"
                    onClick={() => onOpenDeliveryInfo && onOpenDeliveryInfo(order)}
                  >
                    <small>ℹ️</small>
                  </button>
                </>
              )
              : (order.Customer?.name || '—')}
        </span>
        <div className="d-flex align-items-center gap-2">
          {!order.acknowledgedAt && (
            <span className="badge bg-warning text-dark">{t('New', 'Nuevo', lang)}</span>
          )}
          <span className={`badge bg-${cfg?.color || 'secondary'}`}>
            {cfg ? t(cfg.en, cfg.es, lang) : order.status}
          </span>
        </div>
      </div>
      <div className="card-body">
        <div className="small text-muted mb-2">
          <code>{order.orderNumber}</code>
          {order.requestedDate && (
            <span className="ms-2">
              {t('Requested:', 'Solicitado:', lang)} <strong>{order.requestedDate}</strong>
            </span>
          )}
        </div>

        {order.Items?.length > 0 && (
          <ul className="list-unstyled mb-2">
            {order.Items.map((item, i) => (
              <li key={i} className="border-bottom py-1">
                <div className="d-flex justify-content-between">
                  <span>{item.presentationLabel || item.Product?.name || '—'}</span>
                  <span className="fw-bold ms-2">
                    ×{item.quantity}
                    {' '}
                    <span className="text-muted fw-normal small">
                      {item.quantityUnit === 'units' ? t('un', 'un', lang) : t('cs', 'cj', lang)}
                    </span>
                    {item.quantityUnit !== 'units' && item.unitsPerCase ? (
                      <span className="text-muted fw-normal ms-1 small">
                        ({item.unitsPerCase} {t('u/cs', 'u/cj', lang)})
                      </span>
                    ) : null}
                  </span>
                </div>
                <CustomizationDetails item={item} />
              </li>
            ))}
          </ul>
        )}

        {(order.customerNotes || order.notes) && (
          <p className="text-muted small mb-0 fst-italic">
            {order.customerNotes || order.notes}
          </p>
        )}
      </div>
      <div className="card-footer bg-transparent d-flex flex-column gap-2">
        {!order.acknowledgedAt && (
          <button
            className="btn btn-outline-warning btn-sm w-100"
            onClick={handleAcknowledge}
            disabled={acking}
          >
            {acking
              ? <span className="spinner-border spinner-border-sm" />
              : t('Acknowledge', 'Reconocer', lang)
            }
          </button>
        )}
        {order.status === 'ready' && cfg?.nextStatus ? (
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm flex-shrink-0"
              onClick={() => !lotCodesLocked && onOpenLotCodes && onOpenLotCodes(order)}
              disabled={lotCodesLocked}
              title={lotCodesLocked
                ? t('Lot codes approved by QA', 'Lotes aprobados por QA', lang)
                : t('Enter lot codes', 'Ingresar lotes', lang)}
            >
              {lotCodesLocked ? '🔒' : '📦'}
              {!lotCodesLocked && !hasLotCodes && (
                <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 10 }}>!</span>
              )}
            </button>
            <button
              className={`btn btn-${cfg.nextColor} btn-sm flex-grow-1`}
              onClick={handleAdvance}
              disabled={busy}
            >
              {busy
                ? <span className="spinner-border spinner-border-sm" />
                : t(cfg.nextEn, cfg.nextEs, lang)
              }
            </button>
          </div>
        ) : cfg?.nextStatus ? (
          <button
            className={`btn btn-${cfg.nextColor} btn-sm w-100`}
            onClick={handleAdvance}
            disabled={busy}
          >
            {busy
              ? <span className="spinner-border spinner-border-sm" />
              : t(cfg.nextEn, cfg.nextEs, lang)
            }
          </button>
        ) : null}
      </div>
    </div>
  );
}

function HistoryCard({ order, lang }) {
  const cfg = HISTORY_STATUS_LABELS[order.status] || { en: order.status, es: order.status, color: 'secondary' };
  return (
    <div className="card shadow-sm mb-2">
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <span className="fw-semibold">
              {order.eventId
                ? <>{order.Event?.name || `Event #${order.eventId}`} <span className="badge bg-info text-dark ms-1" style={{ fontSize: 10 }}>Direct to Consumer</span></>
                : order.submittedVia === 'shopify'
                  ? <>{order.deliveryContactName || '—'} <span className="badge bg-success ms-1" style={{ fontSize: 10 }}>Shopify</span></>
                  : (order.Customer?.name || '—')}
            </span>
            <code className="text-muted small ms-2">{order.orderNumber}</code>
          </div>
          <span className={`badge bg-${cfg.color}`}>{t(cfg.en, cfg.es, lang)}</span>
        </div>
        {order.requestedDate && (
          <div className="text-muted small mt-1">
            {t('Requested:', 'Solicitado:', lang)} {order.requestedDate}
          </div>
        )}
        {order.Items?.length > 0 && (
          <ul className="list-unstyled mb-0 mt-1">
            {order.Items.map((item, i) => (
              <li key={i} className="small text-muted">
                {item.presentationLabel || item.Product?.name || '—'}
                {' '}×{item.quantity}
                {' '}{item.quantityUnit === 'units' ? t('un', 'un', lang) : t('cs', 'cj', lang)}
                <CustomizationDetails item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function FactoryOrdersPage() {
  const { lang } = useLanguage();
  const { newConfirmedSignal } = useOrderNotifications();
  const [tab, setTab] = useState('active');
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [banner, setBanner] = useState(false);
  const [lotCodeOrder, setLotCodeOrder] = useState(null);
  const [shipOrder,    setShipOrder]    = useState(null);
  const [deliveryInfoOrder, setDeliveryInfoOrder] = useState(null);
  const isFirstSignal = useRef(true);
  const bannerTimerRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customerOrderService.getOrders({ status: 'confirmed,ready' });
      setOrders(data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // When context detects new confirmed orders (on any page), refresh board + show inline banner
  useEffect(() => {
    if (isFirstSignal.current) { isFirstSignal.current = false; return; }
    fetchOrders();
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    setBanner(true);
    bannerTimerRef.current = setTimeout(() => setBanner(false), 12000);
  }, [newConfirmedSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup
  useEffect(() => () => { if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current); }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await customerOrderService.getOrders({ status: 'shipped,invoiced,cancelled' });
      setHistory(data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab, fetchHistory]);

  const handleMarkReady = async (ord) => {
    try {
      await customerOrderService.transitionOrder(ord.id, 'ready');
      await fetchOrders();
      setLotCodeOrder(ord);
    } catch {
      // transition failed — just refresh
      fetchOrders();
    }
  };

  const byStatus = {
    confirmed: orders.filter(o => o.status === 'confirmed'),
    ready: orders.filter(o => o.status === 'ready'),
  };

  const unacknowledgedCount = orders.filter(o => !o.acknowledgedAt).length;

  const columns = [
    { key: 'confirmed', en: 'Confirmed', es: 'Confirmados' },
    { key: 'ready', en: 'Completed', es: 'Completados' },
  ];

  return (
    <div className="container-fluid mt-4">
      {banner && (
        <div className="alert alert-warning alert-dismissible d-flex align-items-center gap-3 mb-3 shadow" role="alert">
          <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>🔔</span>
          <strong>{t('New confirmed order — board updated!', '¡Nuevo pedido confirmado — tablero actualizado!', lang)}</strong>
          <button type="button" className="btn-close ms-auto" onClick={() => setBanner(false)} />
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">{t('Production Floor — Orders', 'Piso de Producción — Pedidos', lang)}</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={fetchOrders} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : t('Refresh', 'Actualizar', lang)}
        </button>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'active' ? ' active' : ''}`}
            onClick={() => setTab('active')}
          >
            {t('Active', 'Activos', lang)}
            {unacknowledgedCount > 0
              ? <span className="badge bg-danger ms-1">{unacknowledgedCount}</span>
              : orders.length > 0 && <span className="badge bg-light text-dark ms-1">{orders.length}</span>
            }
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'history' ? ' active' : ''}`}
            onClick={() => setTab('history')}
          >
            {t('History', 'Historial', lang)}
          </button>
        </li>
      </ul>

      {tab === 'active' && (
        loading && orders.length === 0 ? (
          <div className="text-center py-5"><div className="spinner-border" /></div>
        ) : (
          <div className="row g-3">
            {columns.map(col => (
              <div key={col.key} className="col-md-4">
                <h6 className="fw-semibold text-muted text-uppercase small mb-2">
                  {t(col.en, col.es, lang)}
                  <span className="badge bg-light text-dark ms-2">{byStatus[col.key].length}</span>
                </h6>
                {byStatus[col.key].length === 0 ? (
                  <p className="text-muted fst-italic small">{t('No orders', 'Sin pedidos', lang)}</p>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {byStatus[col.key].map(ord => (
                      <OrderCard
                        key={ord.id}
                        order={ord}
                        onAdvance={fetchOrders}
                        onAcknowledge={fetchOrders}
                        onMarkReady={handleMarkReady}
                        onMarkShipped={setShipOrder}
                        onOpenLotCodes={setLotCodeOrder}
                        onOpenDeliveryInfo={setDeliveryInfoOrder}
                        lang={lang}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'history' && (
        historyLoading ? (
          <div className="text-center py-5"><div className="spinner-border" /></div>
        ) : history.length === 0 ? (
          <p className="text-muted fst-italic">{t('No completed orders yet.', 'No hay pedidos completados aún.', lang)}</p>
        ) : (
          <div>
            {history.map(ord => (
              <HistoryCard key={ord.id} order={ord} lang={lang} />
            ))}
          </div>
        )
      )}

      {lotCodeOrder && (
        <LotCodeModal
          order={lotCodeOrder}
          show={!!lotCodeOrder}
          onClose={() => setLotCodeOrder(null)}
          onSaved={() => { fetchOrders(); setLotCodeOrder(null); }}
          lang={lang}
        />
      )}

      {deliveryInfoOrder && (
        <DeliveryInfoModal order={deliveryInfoOrder} onClose={() => setDeliveryInfoOrder(null)} />
      )}

      {shipOrder && (
        <MarkShippedModal
          order={shipOrder}
          lang={lang}
          onClose={() => setShipOrder(null)}
          onShipped={fetchOrders}
        />
      )}
    </div>
  );
}
